// Fetches the list of games hydra knows about for a given date from the
// analytics endpoint (Postgres-backed, deeper history than the WS cache).
// Returns one entry per game with per-source trade volume — the only
// numeric data point the skinny archived row in GameTable renders.
//
// Why this view consumes analytics: g1's live WS feed is the source of
// truth for live state, but hydra prunes finalized markets from the WS
// cache ~5 min after close. To show closed games on the dashboard
// (especially when the user picks yesterday's date) we have to read
// from the analytics surface. This is a deliberate per-view carve-out
// — see [[feedback_g1_live_feed_only]] and [[project_game_detail_page]]
// for the same pattern in GameDetail. The deeper "with prices" surface
// is tracked in [[project_hydra_ask_analytics_games_with_prices]]; until
// that ships, the dashboard renders volume-only skinny rows.
//
// Dedupe-against-live happens in the caller (ValueDashboard), not here:
// this hook knows nothing about marketStore, returns every game hydra
// has for the date, and the caller drops anything still ingested live.
//
// Performance:
//   - Module-level cache by dateIso so revisiting a date is instant.
//     No TTL — settled historical dates don't change, and today's
//     slight staleness within a session is fine.
//   - In-flight promise dedupe so concurrent mounts (or preloads
//     racing the foreground fetch) only hit hydra once per date.
//   - Adjacent-date preload (±1 day) on date change, fire-and-forget
//     into the cache. Makes left/right navigation through dates feel
//     instant.

import { useEffect, useState } from 'react';
import {
  getGames as getAnalyticsGames,
  hasAnalytics,
  type GameSummary,
} from '@/services/hydraAnalytics';

// Empirical: hydra's analytics surface only returns rows for the last
// ~7 days even though price_ticks is documented as 30-day retention.
// Either the purge runs more aggressively or the docs are stale; either
// way, asking for older dates returns empty so we just cap the window
// here and the date picker effectively can't go further back.
const MAX_LOOKBACK_DAYS = 7;
const DAY_MS = 86_400_000;

const cache = new Map<string, ArchivedGame[]>();
const inflight = new Map<string, Promise<ArchivedGame[]>>();

export interface ArchivedGame {
  /** Hydra canonical: "YYYY-MM-DD|TEAM_A|TEAM_B" (alpha-sorted). Use as
   *  the dedupe key against marketStore-derived GameRow.key via
   *  toHydraGameKey. */
  gameKey: string;
  date: string;
  sport: string;
  /** Alpha-sorted to match canonicalGameKey. Not necessarily away/home;
   *  the skinny row just renders "TEAM_A @ TEAM_B" — there's no per-side
   *  data to attach so the actual game-day order doesn't matter. */
  teamA: string;
  teamB: string;
  kalshiVolume: number;
  polyVolume: number;
}

export interface UseArchivedGamesResult {
  archived: ArchivedGame[];
  loading: boolean;
  error: Error | null;
}

export function useArchivedGames(selectedDateIso: string | null): UseArchivedGamesResult {
  const cached = selectedDateIso ? cache.get(selectedDateIso) : undefined;
  const [state, setState] = useState<UseArchivedGamesResult>(() => ({
    archived: cached ?? [],
    loading: false,
    error: null,
  }));

  useEffect(() => {
    // Skip the fetch on future dates — no historical record exists for
    // games that haven't happened yet, and analytics would return empty.
    if (
      !selectedDateIso ||
      !hasAnalytics() ||
      isFutureDate(selectedDateIso) ||
      isOutsideLookback(selectedDateIso, MAX_LOOKBACK_DAYS)
    ) {
      setState({ archived: [], loading: false, error: null });
      return;
    }

    // Cache hit — render synchronously, no fetch needed.
    const hit = cache.get(selectedDateIso);
    if (hit) {
      setState({ archived: hit, loading: false, error: null });
      // Still fire preloads so adjacent dates land in the cache.
      preloadAdjacent(selectedDateIso);
      return;
    }

    const ctrl = new AbortController();
    let cancelled = false;
    // Clear stale data immediately so the dashboard doesn't render the
    // previous date's archived rows during the seconds it takes for the
    // analytics fetch to land — that flicker looked like a slow filter.
    setState({ archived: [], loading: true, error: null });

    fetchAndCache(selectedDateIso, ctrl.signal)
      .then((archived) => {
        if (cancelled) return;
        setState({ archived, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if ((err as { name?: string })?.name === 'AbortError') return;
        setState({ archived: [], loading: false, error: err as Error });
      });

    preloadAdjacent(selectedDateIso);

    return () => {
      cancelled = true;
      // Don't abort the underlying fetch — its result still benefits the
      // cache for the next mount on this date. Only the local setState
      // is cancelled.
    };
  }, [selectedDateIso]);

  return state;
}

function fetchAndCache(dateIso: string, signal?: AbortSignal): Promise<ArchivedGame[]> {
  // Dedupe concurrent requests for the same date — multiple mounts (or
  // preload racing the foreground fetch) share one upstream request.
  const existing = inflight.get(dateIso);
  if (existing) return existing;

  const promise = getAnalyticsGames({ date: dateIso }, signal)
    .then((res) => {
      const archived = res.items
        .map(toArchivedGame)
        .filter((g): g is ArchivedGame => g !== null);
      cache.set(dateIso, archived);
      return archived;
    })
    .finally(() => {
      inflight.delete(dateIso);
    });

  inflight.set(dateIso, promise);
  return promise;
}

function preloadAdjacent(dateIso: string): void {
  if (!hasAnalytics()) return;
  for (const offset of [-1, 1]) {
    const adjacent = shiftDate(dateIso, offset);
    if (!adjacent) continue;
    if (cache.has(adjacent) || inflight.has(adjacent)) continue;
    if (isOutsideLookback(adjacent, MAX_LOOKBACK_DAYS)) continue;
    // Future dates are pointless to preload — analytics won't have rows
    // for games that haven't happened yet.
    if (isFutureDate(adjacent)) continue;
    fetchAndCache(adjacent).catch(() => {
      // Background fetch — failure is silent; the foreground hook will
      // surface real errors when the user actually navigates there.
    });
  }
}

function shiftDate(dateIso: string, days: number): string | null {
  const ms = new Date(`${dateIso}T00:00:00`).getTime();
  if (!Number.isFinite(ms)) return null;
  const d = new Date(ms + days * DAY_MS);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function toArchivedGame(g: GameSummary): ArchivedGame | null {
  if (!g.gameKey || !g.teams || g.teams.length < 2) return null;
  const [teamA, teamB] = [g.teams[0], g.teams[1]].map((t) => t.toUpperCase()).sort();
  let kalshiVolume = 0;
  let polyVolume = 0;
  for (const t of g.trades ?? []) {
    if (t.source === 'kalshi') kalshiVolume += t.totalSize;
    else if (t.source === 'polymarket') polyVolume += t.totalSize;
  }
  return {
    gameKey: g.gameKey,
    date: g.date,
    sport: (g.sport ?? '').toLowerCase(),
    teamA,
    teamB,
    kalshiVolume,
    polyVolume,
  };
}

function isOutsideLookback(dateIso: string, days: number): boolean {
  const target = new Date(`${dateIso}T00:00:00`).getTime();
  if (!Number.isFinite(target)) return true;
  return target < Date.now() - days * DAY_MS;
}

// True when the dateIso refers to a calendar day after today (local
// time). Lexicographic compare on YYYY-MM-DD works because the format
// is monotonic. selectedDate from the dashboard is local-time mdy →
// iso so this stays consistent with what the user sees in the picker.
function isFutureDate(dateIso: string): boolean {
  const now = new Date();
  const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return dateIso > todayIso;
}
